import { useCallback, useMemo, useRef, useState, type KeyboardEvent, type RefObject } from 'react'
import { applySuggestion, findTrigger, toTokens, type Suggestion, type Trigger } from '@/lib/autocomplete'

interface Options {
  textarea: RefObject<HTMLTextAreaElement | null>
  draft: string
  setDraft: (v: string) => void
  /** Кандидаты по триггеру: '@' — участники и роли, '#' — каналы. */
  suggest: (t: Trigger) => Suggestion[]
  /** Карта подстановок снаружи — у черновика канала своя, и она переживает смену канала. */
  picked?: Map<string, string> | undefined
}

/** Состояние автодополнения: следит за курсором, подставляет выбранное, обрабатывает стрелки/Enter/Tab/Esc. */
export function useMentions({ textarea, draft, setDraft, suggest, picked: shared }: Options) {
  const [trigger, setTrigger] = useState<Trigger | null>(null)
  // что человек выбрал в подсказке: «@Ник» → «<@id>». В поле остаётся читаемое,
  // токены подставляются перед отправкой
  const picked = useRef(shared ?? new Map<string, string>())
  const [active, setActive] = useState(0)
  const items = useMemo(() => (trigger ? suggest(trigger).slice(0, 10) : []), [trigger, suggest])

  const refresh = useCallback(
    (text: string, caret: number) => {
      const t = findTrigger(text, caret)
      setTrigger(t)
      setActive(0)
    },
    [],
  )

  const pick = useCallback(
    (s: Suggestion) => {
      const el = textarea.current
      if (!trigger) return
      const caret = el ? el.selectionStart : draft.length
      const shown = s.text ?? s.insert
      if (shown !== s.insert) picked.current.set(shown, s.insert)
      const next = applySuggestion(draft, trigger, caret, shown)
      setDraft(next.text)
      setTrigger(null)
      requestAnimationFrame(() => {
        if (el) {
          el.focus()
          el.setSelectionRange(next.caret, next.caret)
        }
      })
    },
    [draft, setDraft, textarea, trigger],
  )

  /** true — событие обработано автодополнением, дальше не передавать. */
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (!trigger || !items.length) return false
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActive((a) => (a + 1) % items.length)
        return true
      case 'ArrowUp':
        e.preventDefault()
        setActive((a) => (a - 1 + items.length) % items.length)
        return true
      case 'Enter':
      case 'Tab': {
        e.preventDefault()
        const s = items[active]
        if (s) pick(s)
        return true
      }
      case 'Escape':
        e.preventDefault()
        setTrigger(null)
        return true
      default:
        return false
    }
  }

  /** Текст для отправки: читаемые упоминания заменяются токенами. */
  const toServer = useCallback((text: string) => toTokens(text, picked.current), [])

  return { items, active, setActive, trigger, refresh, pick, onKeyDown, toServer, close: () => setTrigger(null) }
}
